/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * PlaceSearch
 * -----------
 * "Where does the course start?" answered by typing, not by zooming.
 *
 * The same Nominatim search the form's address fields use (geocode.js), with
 * the same 3-character minimum and 500 ms debounce — the public server allows
 * about one request a second, and a lookup per keystroke would break that.
 *
 * It only reports the chosen place; what happens next (move the map, set the
 * start) is RouteDrawer's decision.
 */

import React, { useEffect, useRef, useState } from 'react';
import { geocode } from '../../athlohive/geocode';

const DEBOUNCE_MS = 500;
const MIN_QUERY_LENGTH = 3;

export default function PlaceSearch({ onPick, placeholder = 'Search for a place, e.g. Cubbon Park, Bengaluru' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  // Set by typing, cleared by choosing: a chosen label in the box must not
  // start a new search for itself.
  const typedRef = useRef(false);
  const abortRef = useRef(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (!typedRef.current) return undefined;
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setSearched(false);
      return undefined;
    }
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setSearching(true);
      const found = await geocode(trimmed, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setResults(found);
      setSearched(true);
      setSearching(false);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const choose = (match) => {
    typedRef.current = false;
    setQuery(match.label);
    setResults([]);
    setSearched(false);
    onPick({ lat: Number(match.lat), lng: Number(match.lng), label: match.label });
  };

  const onKeyDown = (event) => {
    // Enter picks the top match rather than submitting the event form behind.
    if (event.key === 'Enter') {
      event.preventDefault();
      if (results.length) choose(results[0]);
    }
    if (event.key === 'Escape' && results.length) {
      // Close the list, not the modal around it.
      event.stopPropagation();
      setResults([]);
    }
  };

  return (
    <div className="orgr-search">
      <div className="orgf-searchwrap">
        <i className="bi bi-search orgr-search__icon" aria-hidden="true" />
        <input
          type="search"
          className="orgf-input orgr-search__input"
          value={query}
          placeholder={placeholder}
          aria-label="Search for a place on the map"
          autoComplete="off"
          onChange={(event) => { typedRef.current = true; setQuery(event.target.value); }}
          onKeyDown={onKeyDown}
        />
        {results.length > 0 && (
          <ul className="orgf-results" role="listbox" aria-label="Places found">
            {results.map((match) => (
              <li key={`${match.lat},${match.lng},${match.label}`} role="option" aria-selected="false">
                <button type="button" onClick={() => choose(match)}>
                  <span className="orgf-pin" aria-hidden="true">◉</span>
                  <span>
                    {match.label}
                    <small>{match.lat}, {match.lng}</small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {searching && <span className="orgf-help">Searching…</span>}
      {!searching && searched && results.length === 0 && (
        <span className="orgf-help">No places found. Try a nearby landmark or area name.</span>
      )}
    </div>
  );
}
